// ───────────────────────────────────────────────────────────────────
// Product analytics (Firebase / GA4) for the employer web app.
//
// One typed chokepoint so feature code never imports firebase directly. Event
// + property names come from @chotca/config, shared with the worker mobile app
// so the marketplace funnel (apply → invite → commit → interview) joins across
// both sides.
//
// Init is async (getAnalytics needs isSupported + a measurementId). Identity is
// buffered and applied once ready so an early setIdentity call isn't lost.
// Events fired before init is ready are dropped (all backbone events happen
// well after boot, post user-interaction). If VITE_FIREBASE_* is unset, every
// call is a silent no-op.
//
// Privacy: never pass email / phone / name. Only ids + coarse buckets.
// ───────────────────────────────────────────────────────────────────

import { initializeApp } from "firebase/app";
import {
  getAnalytics,
  isSupported,
  logEvent as fbLogEvent,
  setUserId as fbSetUserId,
  setUserProperties as fbSetUserProperties,
  type Analytics,
} from "firebase/analytics";
import type { AnalyticsEventName, AnalyticsParams } from "@chotca/config";
import { FIREBASE_CONFIG } from "@/env";

let analytics: Analytics | null = null;
let initStarted = false;

// Buffered identity, applied when analytics becomes ready.
let pendingUserId: string | null = null;
let pendingProps: Record<string, string> | null = null;

export function initAnalytics(): void {
  if (initStarted) return;
  initStarted = true;
  if (!FIREBASE_CONFIG.apiKey || !FIREBASE_CONFIG.measurementId) return;
  void isSupported()
    .then((ok: boolean) => {
      if (!ok) return;
      const app = initializeApp(FIREBASE_CONFIG);
      analytics = getAnalytics(app);
      if (pendingUserId !== null) fbSetUserId(analytics, pendingUserId);
      if (pendingProps) fbSetUserProperties(analytics, pendingProps);
    })
    .catch(() => {
      /* analytics unsupported (e.g. SSR / no cookies) — stay disabled */
    });
}

function clean(params?: AnalyticsParams): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  if (!params) return out;
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    if (typeof v === "boolean") out[k] = v ? "true" : "false";
    else if (typeof v === "number") out[k] = v;
    else out[k] = String(v).slice(0, 100);
  }
  return out;
}

export function logAnalyticsEvent(
  name: AnalyticsEventName,
  params?: AnalyticsParams,
): void {
  if (!analytics) return;
  try {
    fbLogEvent(analytics, name, clean(params));
  } catch {
    /* never let analytics break a feature path */
  }
}

export function setAnalyticsIdentity(
  id: string | null,
  props?: Record<string, string | null | undefined>,
): void {
  const cleanProps: Record<string, string> = {};
  if (props) {
    for (const [k, v] of Object.entries(props)) {
      if (v) cleanProps[k] = v.slice(0, 36);
    }
  }
  if (analytics) {
    fbSetUserId(analytics, id);
    fbSetUserProperties(analytics, cleanProps);
  } else {
    pendingUserId = id;
    pendingProps = cleanProps;
  }
}
